import type { QuickLauncherPosition } from "./quick-launcher.slice";

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * Pulls a restored drag offset back inside the viewport.
 *
 * `rect` is the panel's measured position with `position` already applied, so
 * the correction is computed as a delta and added back onto the stored offset.
 *
 * The case this exists for: drag the launcher to the bottom-right of a large
 * monitor, then open it on a laptop. The saved translate puts the panel past the
 * window edge, where it cannot be seen — and therefore cannot be dragged back.
 *
 * Overflow on the leading edge wins over the trailing edge (`Math.max` /
 * `Math.min` against the negative of the leading gap), so a panel larger than
 * the viewport pins to the top-left and stays reachable instead of being pushed
 * off the other side.
 */
export default function clampPosition(
  position: QuickLauncherPosition,
  rect: Rect,
  viewport: Viewport
): QuickLauncherPosition {
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;

  let dx = 0;
  if (rect.left < 0) dx = -rect.left;
  else if (right > viewport.width) {
    dx = Math.max(viewport.width - right, -rect.left);
  }

  let dy = 0;
  if (rect.top < 0) dy = -rect.top;
  else if (bottom > viewport.height) {
    dy = Math.max(viewport.height - bottom, -rect.top);
  }

  if (dx === 0 && dy === 0) return position;
  return { x: position.x + dx, y: position.y + dy };
}
